/**
 * @fileoverview Loading skeleton component displayed when Continue button is clicked
 */

import React from 'react';
import TrebbleSpace from '../../common/TrebbleSpace.tsx';
import TrebbleSkeleton from '../../common/TrebbleSkeleton.tsx';
// @ts-ignore
import ti18n from 'i18n!nls/Deepcuti18n';

/**
 * Loading skeleton component displayed while processing between wizard steps
 * @param {Object} props - Component props
 * @param {string} props.currentStep - Current wizard step ('goal' or 'questions')
 * @param {string} [props.customMessage] - Optional custom loading message
 * @param {boolean} [props.showLoadingMessage=true] - Whether to show loading text and decorative blocks
 * @param {boolean} [props.useEditorTheme] - Whether to use editor theme for components
 * @returns {React.ReactElement}
 */
export default function ContinueLoadingSkeleton({ currentStep, customMessage, showLoadingMessage = true, useEditorTheme }) {
  const t = (k) => (window.getI18n ? window.getI18n(ti18n, k) : '');
  const getStepMessage = () => {
    if (customMessage) return customMessage;
    if (currentStep === 'goal') return t('CONTINUE_LOADING_GOAL_TO_QUESTIONS');
    if (currentStep === 'questions') return t('CONTINUE_LOADING_QUESTIONS_TO_PLAN');
    return t('CONTINUE_LOADING_PROCESSING');
  };
  const getTimeExpectation = () => t('CONTINUE_LOADING_TIME_EXPECTATION');

  return (
    <TrebbleSpace direction="vertical" className="w-full" useEditorTheme={useEditorTheme}>
      {/* Form skeleton with centered loading message */}
      <div className="opacity-60">
        {/* Title skeleton */}
        <div className="mb-6">
          <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '60%' }} title={false} />
        </div>

        {/* Question 1 skeleton */}
        <div className="mb-6">
          <div className="mb-3">
            <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '45%' }} title={false} />
          </div>
          <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '85%' }} title={false} />
        </div>

        {/* Question 2 skeleton */}
        <div className="mb-6">
          <div className="mb-3">
            <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '52%' }} title={false} />
          </div>
          <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '85%' }} title={false} />
        </div>
      </div>

      {/* Loading message centered within skeleton content - no border */}
      {showLoadingMessage && (
        <div className="my-5 text-center">
          {/* Complete loading section with decorative skeleton blocks */}
          <div className="flex items-center justify-start max-w-full">
            {/* Left shimmer block */}
            <div className="mr-5 w-[180px] shrink-0">
              <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 4, width: ['85%', '100%', '70%', '90%'] }} title={false} />
            </div>
            
            {/* Central loading text block */}
            <div className="text-center max-w-[320px] flex-1">
              {/* Main loading message */}
              <div className="text-lg font-bold leading-relaxed mb-4 break-words animate-shimmer-text">
                {getStepMessage()}
              </div>
              
              {/* Time expectation text */}
              <div className="text-sm leading-relaxed opacity-80 break-words animate-shimmer-text">
                {getTimeExpectation()}
              </div>
            </div>
            
            {/* Right shimmer block */}
            <div className="ml-5 w-[180px] shrink-0">
              <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 4, width: ['75%', '90%', '65%', '100%'] }} title={false} />
            </div>
          </div>
        </div>
      )}

      {/* Continue with remaining skeleton content */}
      <div className="opacity-60">
        {/* Question 3 skeleton */}
        <div className="mb-6">
          <div className="mb-3">
            <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '48%' }} title={false} />
          </div>
          <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '85%' }} title={false} />
        </div>

        {/* Question 4 skeleton */}
        <div className="mb-6">
          <div className="mb-3">
            <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '58%' }} title={false} />
          </div>
          <div className="w-full">
            <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '90%' }} className="mb-3" title={false} />
            <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '85%' }} className="mb-3" title={false} />
            <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '60%' }} title={false} />
          </div>
        </div>
      </div>
    </TrebbleSpace>
  );
}